import {
  Button,
  Column,
  Columns,
  EmptyState,
  FlexBox,
  HelpIcon,
  IconButton,
  Text,
  Tooltip,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import { client as elevioClient } from 'elevio';
import { Controls, QuickView as QuickViewT } from 'lib-analytics-types';
import {
  AnalyticsMenu,
  type AnalyticsMenuActions,
  formatMillisecondsToDuration,
} from 'lib-analytics-ui';
import { useHasAccess } from 'lib-shared-access-control';
import { ampli } from 'lib-shared-tracking';
import { ReactNode, useMemo } from 'react';
import { useInterval } from 'react-use';

import { articles } from 'utils/elevio';
import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import { useAppDispatch } from 'utils/redux/useAppDispatch';
import { useAppSelector } from 'utils/redux/useAppSelector';
import { useAppNavigate } from 'utils/url/useAppNavigate';
import { useServerStorage } from 'utils/useServerStorage';
import { useShortHumanReadableTimer } from 'utils/useShortHumanReadableTimer';
import { useCurrentUser } from 'utils/user/useCurrentUser';

import { analyticsActions } from '../../../../analytics.actions';
import { AnalyticsHeader } from '../../../../components/header/analyticsHeader';
import { AnalyticsPanels } from '../../../../components/panels/analyticsPanels';
import { AnalyticsQuickViewFavoriteToggle } from '../../../../components/quickView/components/favorite/quickViewFavoriteToggle';

import { QuickViewControls } from './components/controls/quickViewControls';
import styles from './quickView.module.scss';
import { useQuickViewActions } from './utils/useQuickViewActions/useQuickViewActions';
import { useQuickViewManager } from './utils/useQuickViewManager/useQuickViewManager';

type QuickViewProps = {
  quickView: QuickViewT;
};

export const QuickView = ({ quickView }: QuickViewProps): JSX.Element => {
  const dispatch = useAppDispatch();
  const navigate = useAppNavigate();
  const { user } = useCurrentUser();

  const {
    handleUpdateQuickView,
    handleDuplicateQuickView,
    handleDeleteQuickView,
    handleUpdateQuickViewFavoriteState,
    handleTransferQuickViewOwnership,
    handleToggleQuickViewAsLandingPage,
  } = useQuickViewActions();

  const {
    controls,
    panels,
    hasUnsavedChanges,
    handleUnpinPanel,
    handleUndoChanges,
    handleChangeLayout,
    handleChangePanelControls,
    handleChangeQuickViewControls,
    handleSaveChanges,
  } = useQuickViewManager(quickView);

  const { ui } = useAppSelector((state) => state.analytics);
  const hasAnalyticsAccess = useHasAccess('analytics');
  const flags = useFeatureFlag(['analytics-custom-dashboards']);
  const storage = useServerStorage('user');
  const isLandingPage = storage.data.analytics?.landingPageId === quickView.id;
  const refreshInterval =
    (flags.analyticsCustomDashboards && quickView.options?.refresh_interval) ||
    null;

  useInterval(() => {
    if (refreshInterval) {
      dispatch(analyticsActions.forceRefresh());

      ampli.analyticsQuickViewAutoRefreshTriggered({
        interval: formatMillisecondsToDuration(refreshInterval, {
          compact: true,
        }),
        shared: quickView.shared,
      });
    }
  }, refreshInterval);

  /* The relative time of the last time the quick view data was refreshed. */
  const lastRefreshTime = useShortHumanReadableTimer(ui.lastRefresh);

  /** Only the owner of the quick view can modify its details and visibility. */
  const isOwner = quickView.user.id === user.id;

  /**
   * Only the owner of the quick view can customize its panels through resize,
   * drag and drop or editing the description.
   */
  const canCustomizePanels = flags.analyticsCustomDashboards && isOwner;

  /**
   * Only the owner and users with full access to Analytics can transfer
   * ownership of quick views that are shared.
   */
  const canTransferOwnership =
    (isOwner || hasAnalyticsAccess) && !!quickView.shared;

  /* The list of actions available for the current quick view. */
  const actions: AnalyticsMenuActions = [
    ...(isOwner
      ? ([
          {
            type: 'button',
            icon: 'pen',
            label: 'Edit',
            onClick: () => quickView && handleUpdateQuickView(quickView),
          },
        ] as AnalyticsMenuActions)
      : []),
    {
      type: 'button',
      icon: 'copy',
      label: 'Duplicate',
      onClick: () => quickView && handleDuplicateQuickView(quickView),
    },
    ...(flags.analyticsCustomDashboards
      ? ([
          {
            type: 'button',
            icon: isLandingPage ? 'link-slash' : 'link',
            label: isLandingPage
              ? 'Remove as my landing page'
              : 'Set as my landing page',
            tooltip: isLandingPage
              ? 'This dashboard will be removed as your landing page and will no longer be shown by default when you navigate to Analytics.'
              : 'This dashboard will be marked as favorite and shown by default the first time you navigate to Analytics.',
            onClick: async () =>
              quickView && handleToggleQuickViewAsLandingPage(quickView),
          },
        ] as AnalyticsMenuActions)
      : []),
    ...(canTransferOwnership
      ? ([
          {
            type: 'divider',
          },
          {
            type: 'button',
            icon: 'exchange',
            label: 'Transfer ownership',
            onClick: () =>
              quickView && handleTransferQuickViewOwnership(quickView),
          },
        ] as AnalyticsMenuActions)
      : []),
    ...(isOwner
      ? ([
          {
            type: 'divider',
          },
          {
            type: 'button',
            icon: 'trash',
            label: 'Delete',
            disabled: hasUnsavedChanges,
            tooltip: hasUnsavedChanges
              ? 'You have unsaved changes. Save or undo them before deleting.'
              : undefined,
            onClick: async () => {
              if (quickView) {
                const { isDone } = await handleDeleteQuickView(quickView);
                isDone && navigate({ path: '/analytics/dashboards' });
              }
            },
          },
        ] as AnalyticsMenuActions)
      : []),
    {
      type: 'divider',
    },
    {
      type: 'button',
      icon: 'sync',
      label: 'Refresh data',
      description: `Last refreshed ${lastRefreshTime}`,
      disabled: false,
      onClick: () => {
        dispatch(analyticsActions.forceRefresh());
        ampli.analyticsRefreshClicked();
      },
    },
  ];

  /** The map of panel controls that should be disabled and the reason. */
  const disabledPanelControls = useMemo(() => {
    const map = new Map<keyof Controls, ReactNode>();
    const hasGlobalPeriod = !!controls?.period;

    if (hasGlobalPeriod) {
      map.set(
        'period',
        'The selected global period is automatically applied to all panels. To change the period for this panel, first remove the global period.',
      );
    }
    return map;
  }, [controls?.period]);

  return (
    <Columns>
      <Column>
        <Column.Row slottable borderBottom>
          <Column.Slot>
            <AnalyticsHeader tier={ui.tier}>
              {quickView && (
                <>
                  <Text.Heading color="primary" as="h2">
                    {quickView.name}
                  </Text.Heading>

                  {quickView.description && (
                    <HelpIcon text={quickView.description} />
                  )}

                  <Tooltip
                    content={
                      quickView.favorited
                        ? 'Remove from the sidebar'
                        : 'Add to the sidebar'
                    }
                  >
                    <AnalyticsQuickViewFavoriteToggle
                      favorited={quickView.favorited}
                      onClick={() =>
                        handleUpdateQuickViewFavoriteState(quickView)
                      }
                    />
                  </Tooltip>

                  {refreshInterval && (
                    <Tooltip
                      content={
                        <>
                          Auto refreshes every{' '}
                          {formatMillisecondsToDuration(refreshInterval, {
                            compact: true,
                          })}
                          .{' '}
                          {isOwner && (
                            <Text.ExternalLink
                              onClick={() => handleUpdateQuickView(quickView)}
                            >
                              Change
                            </Text.ExternalLink>
                          )}
                        </>
                      }
                      requireClick
                      side="right"
                    >
                      <div className={styles.live} />
                    </Tooltip>
                  )}
                </>
              )}
            </AnalyticsHeader>
          </Column.Slot>

          <Column.Slot noVerticalPadding placement="end">
            <FlexBox alignItems="center" gap="200">
              {isOwner && (
                <FlexBox alignItems="center" gap="250">
                  <Button
                    variant="primary"
                    disabled={!hasUnsavedChanges}
                    onClick={handleSaveChanges}
                    className={classNames(styles.save, {
                      [styles.savePulse]: hasUnsavedChanges,
                    })}
                  >
                    Save
                  </Button>

                  <Tooltip
                    content="Undo unsaved changes"
                    disabled={!hasUnsavedChanges}
                  >
                    <IconButton
                      icon="undo"
                      variant="tertiary"
                      disabled={!hasUnsavedChanges}
                      onClick={handleUndoChanges}
                    />
                  </Tooltip>
                </FlexBox>
              )}

              <AnalyticsMenu actions={actions} />
            </FlexBox>
          </Column.Slot>
        </Column.Row>

        <Column.Row scrollable>
          <>
            {panels.length ? (
              <FlexBox flexDirection="column" gap="300">
                <QuickViewControls
                  controls={controls}
                  onChangeControls={handleChangeQuickViewControls}
                />

                <AnalyticsPanels
                  panels={panels}
                  isCustomizable={canCustomizePanels}
                  disabledControls={disabledPanelControls}
                  onChangeControls={handleChangePanelControls}
                  {...(canCustomizePanels && {
                    onUnpin: handleUnpinPanel,
                    onChangeLayout: handleChangeLayout,
                  })}
                />
              </FlexBox>
            ) : (
              <FlexBox
                height="100%"
                alignItems="center"
                justifyContent="center"
              >
                <EmptyState
                  title="This dashboard is empty"
                  description="Get started by adding panels to this dashboard from the existing dashboards."
                  icon="chart-bar"
                  buttonProps={{
                    variant: 'primary',
                    children: 'Learn more',
                    onClick: () =>
                      elevioClient.openArticle(articles.analytics.quickViews),
                  }}
                />
              </FlexBox>
            )}
          </>
        </Column.Row>
      </Column>
    </Columns>
  );
};
